import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CalendarOutlined,
  CloseOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Flex, Spin } from "antd";

import { appointmentDetailsCleared, loadAppointmentDetails } from "../../slice";
import { selectAppointmentDetails } from "../../selector";
import { cleanValue } from "../../../../../utils/cleanValue";
import { formatUkDateTime } from "../../../../../utils/datetime";
import AudioPlayer from "../../../../components/AudioPlayer";
import type { Appointment } from "../../types";
import {
  ApptDetailField,
  ApptDetailGrid,
  ApptDetailIconBox,
  ApptDetailLabel,
  ApptDetailValue,
  ApptModal,
  ApptModalBody,
  ApptModalClose,
  ApptModalDivider,
  ApptModalHeader,
  ApptModalHeaderIcon,
  ApptModalSubtitle,
  ApptModalTitle,
  ApptStatusLabel,
  ApptStatusRow,
  ApptSummaryBox,
  ApptSummaryLabel,
  ApptSummaryText,
  StatusTag,
} from "../../style";

interface AppointmentDetailModalProps {
  appointment: Appointment | null;
  open: boolean;
  onClose: () => void;
}

export default function AppointmentDetailModal({
  appointment,
  open,
  onClose,
}: AppointmentDetailModalProps) {
  const dispatch = useDispatch();
  const { data: details, loading } = useSelector(selectAppointmentDetails);

  useEffect(() => {
    if (!open || !appointment) return;
    if (!appointment.appointmentId) return;
    dispatch(loadAppointmentDetails({ appointmentId: appointment.appointmentId }));
    return () => {
      dispatch(appointmentDetailsCleared());
    };
  }, [open, appointment, dispatch]);

  if (!appointment) return null;

  const name = cleanValue(details?.full_name) || appointment.patientName;
  const phone = cleanValue(details?.phone_number) || appointment.patientPhone;
  const service = cleanValue(details?.service) || appointment.service;
  const status = cleanValue(details?.status) || appointment.status;
  const summary = cleanValue(details?.call_summary);
  const recordingUrl = cleanValue(details?.recording_url) || null;
  const fallbackDateTime = `${appointment.date} at ${appointment.time}`;
  const dateTime = details?.created_at
    ? formatUkDateTime(details.created_at, fallbackDateTime)
    : fallbackDateTime;

  return (
    <ApptModal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={560}
      destroyOnHidden
    >
      <ApptModalHeader align="center" gap={12}>
        <ApptModalHeaderIcon align="center" justify="center">
          <MedicineBoxOutlined />
        </ApptModalHeaderIcon>
        <Flex vertical gap={1} flex={1}>
          <ApptModalTitle level={4}>Appointment Details</ApptModalTitle>
          <ApptModalSubtitle>
            Full information for this appointment
          </ApptModalSubtitle>
        </Flex>
        <ApptModalClose
          aria-label="Close"
          icon={<CloseOutlined />}
          onClick={onClose}
        />
      </ApptModalHeader>
      <ApptModalDivider />
      <Spin spinning={loading}>
        <ApptModalBody vertical gap={22}>
          <ApptDetailGrid wrap="wrap" gap={24}>
            <ApptDetailField align="center" gap={12}>
              <ApptDetailIconBox align="center" justify="center">
                <UserOutlined />
              </ApptDetailIconBox>
              <Flex vertical gap={1}>
                <ApptDetailLabel>Patient Name</ApptDetailLabel>
                <ApptDetailValue>{name}</ApptDetailValue>
              </Flex>
            </ApptDetailField>
            <ApptDetailField align="center" gap={12}>
              <ApptDetailIconBox align="center" justify="center">
                <PhoneOutlined />
              </ApptDetailIconBox>
              <Flex vertical gap={1}>
                <ApptDetailLabel>Phone Number</ApptDetailLabel>
                <ApptDetailValue>{phone}</ApptDetailValue>
              </Flex>
            </ApptDetailField>
            <ApptDetailField align="center" gap={12}>
              <ApptDetailIconBox align="center" justify="center">
                <MedicineBoxOutlined />
              </ApptDetailIconBox>
              <Flex vertical gap={1}>
                <ApptDetailLabel>Service</ApptDetailLabel>
                <ApptDetailValue>{service}</ApptDetailValue>
              </Flex>
            </ApptDetailField>
            <ApptDetailField align="center" gap={12}>
              <ApptDetailIconBox align="center" justify="center">
                <CalendarOutlined />
              </ApptDetailIconBox>
              <Flex vertical gap={1}>
                <ApptDetailLabel>Date &amp; Time</ApptDetailLabel>
                <ApptDetailValue>{dateTime}</ApptDetailValue>
              </Flex>
            </ApptDetailField>
          </ApptDetailGrid>

          {recordingUrl && <AudioPlayer src={recordingUrl} />}

          <ApptSummaryBox vertical gap={8}>
            <ApptSummaryLabel>Call Summary</ApptSummaryLabel>
            <ApptSummaryText>
              {summary || "No summary available."}
            </ApptSummaryText>
          </ApptSummaryBox>

          <ApptStatusRow align="center" gap={16}>
            <ApptStatusLabel>Status</ApptStatusLabel>
            <StatusTag $status={status}>{status}</StatusTag>
          </ApptStatusRow>
        </ApptModalBody>
      </Spin>
    </ApptModal>
  );
}
